import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Checkbox,
    ChoiceGroup,
    ComboBox,
    DefaultButton,
    IChoiceGroupOption,
    IComboBoxOption,
    IModalStyles,
    MessageBar,
    MessageBarType,
    Modal,
    PrimaryButton,
    SearchBox,
    Text,
} from "@fluentui/react";
import { boardContext } from "../../providers/boardContext";
import { TBoardContextType } from "../../interfaces/boardContext";
import { normalizeSiteIdList } from "../../helpers/boardSelection";

const modalStyles: Partial<IModalStyles> = {
    main: {
        width: 'min(860px, calc(100vw - 24px))',
        maxWidth: 'min(860px, calc(100vw - 24px))',
        maxHeight: '90vh',
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
    },
    scrollableContent: {
        maxHeight: '90vh',
    }
};

const modeOptions: IChoiceGroupOption[] = [
    { key: 'site', text: 'Site Location' },
    { key: 'region', text: 'Region' },
];

const modalShellStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    background: 'linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%)',
};

const contentStyles: React.CSSProperties = {
    display: 'grid',
    gap: 18,
    padding: 24,
};

const cardStyles: React.CSSProperties = {
    border: '1px solid #dbe2ea',
    borderRadius: 14,
    padding: 16,
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
};

const sectionTitleStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
};

const checkboxListStyles: React.CSSProperties = {
    maxHeight: 280,
    overflowY: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 10,
    background: '#fbfdff',
};

const getInputValue = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    return String(newValue ?? event?.target?.value ?? '');
};

const BoardSelectionModal = () => {
    const {
        applySelection,
        availableSites,
        closeSelector,
        contextId,
        hasActiveSelection,
        regions,
        selectorBlocking,
        selectorOpen,
        selectionMode,
        siteIds,
    } = useContext(boardContext);

    const [draftMode, setDraftMode] = useState<TBoardContextType>('site');
    const [draftSiteId, setDraftSiteId] = useState('');
    const [draftRegionId, setDraftRegionId] = useState('');
    const [draftSelectedSiteIds, setDraftSelectedSiteIds] = useState<string[]>([]);
    const [siteSearch, setSiteSearch] = useState('');
    const [regionSearch, setRegionSearch] = useState('');
    const [regionSiteSearch, setRegionSiteSearch] = useState('');
    const [error, setError] = useState('');

    const filteredSiteOptions = useMemo<IComboBoxOption[]>(() => {
        const normalizedSearch = siteSearch.trim().toLowerCase();
        return availableSites
            .filter((site) => {
                if (!normalizedSearch) {
                    return true;
                }

                return String(site.illumina_name ?? '').toLowerCase().includes(normalizedSearch);
            })
            .map((site) => ({
                key: site.illumina_sitelocationid,
                text: site.illumina_name,
            }));
    }, [availableSites, siteSearch]);

    const filteredRegionOptions = useMemo<IComboBoxOption[]>(() => {
        const normalizedSearch = regionSearch.trim().toLowerCase();
        return regions
            .filter((region) => {
                if (!normalizedSearch) {
                    return true;
                }

                return String(region.illumina_name ?? '').toLowerCase().includes(normalizedSearch);
            })
            .map((region) => ({
                key: String(region.illumina_regionid ?? ''),
                text: region.siteCount
                    ? `${region.illumina_name} (${region.siteCount})`
                    : String(region.illumina_name ?? 'Region'),
            }));
    }, [regionSearch, regions]);

    const selectedSiteOption = useMemo(() => {
        return availableSites.find((site) => site.illumina_sitelocationid === draftSiteId);
    }, [availableSites, draftSiteId]);

    const selectedRegionOption = useMemo(() => {
        return regions.find((region) => String(region.illumina_regionid ?? '') === draftRegionId);
    }, [draftRegionId, regions]);

    const regionSites = useMemo(() => {
        if (!draftRegionId) {
            return [];
        }

        return availableSites.filter((site) => String(site._illumina_region_value ?? '').trim() === draftRegionId);
    }, [availableSites, draftRegionId]);

    const filteredRegionSites = useMemo(() => {
        const normalizedSearch = regionSiteSearch.trim().toLowerCase();
        return regionSites.filter((site) => {
            if (!normalizedSearch) {
                return true;
            }

            return String(site.illumina_name ?? '').toLowerCase().includes(normalizedSearch);
        });
    }, [regionSiteSearch, regionSites]);

    useEffect(() => {
        if (!selectorOpen) {
            return;
        }

        const nextMode = hasActiveSelection ? selectionMode : 'site';
        setDraftMode(nextMode);
        setDraftSiteId(hasActiveSelection && selectionMode === 'site' ? contextId : '');
        setDraftRegionId(hasActiveSelection && selectionMode === 'region' ? contextId : '');
        setDraftSelectedSiteIds(hasActiveSelection && selectionMode === 'region' ? siteIds : []);
        setSiteSearch(
            hasActiveSelection && selectionMode === 'site'
                ? String(availableSites.find((site) => site.illumina_sitelocationid === contextId)?.illumina_name ?? '')
                : ''
        );
        setRegionSearch(
            hasActiveSelection && selectionMode === 'region'
                ? String(regions.find((region) => String(region.illumina_regionid ?? '') === contextId)?.illumina_name ?? '')
                : ''
        );
        setRegionSiteSearch('');
        setError('');
    }, [availableSites, contextId, hasActiveSelection, regions, selectorOpen, selectionMode, siteIds]);

    useEffect(() => {
        if (draftMode !== 'region' || !draftRegionId) {
            return;
        }

        const validSiteIds = new Set(regionSites.map((site) => site.illumina_sitelocationid));
        setDraftSelectedSiteIds((previous) => {
            const filteredIds = normalizeSiteIdList(previous).filter((siteId) => validSiteIds.has(siteId));
            if (filteredIds.length > 0) {
                return filteredIds;
            }

            return regionSites.map((site) => site.illumina_sitelocationid);
        });
    }, [draftMode, draftRegionId, regionSites]);

    const toggleRegionSite = (siteId: string, checked?: boolean) => {
        setDraftSelectedSiteIds((previous) => {
            const currentIds = new Set(previous);
            if (checked) {
                currentIds.add(siteId);
            } else {
                currentIds.delete(siteId);
            }

            return normalizeSiteIdList(Array.from(currentIds));
        });
    };

    const selectAllRegionSites = () => {
        setDraftSelectedSiteIds(regionSites.map((site) => site.illumina_sitelocationid));
    };

    const clearAllRegionSites = () => {
        setDraftSelectedSiteIds([]);
    };

    const canApply = draftMode === 'site'
        ? Boolean(draftSiteId)
        : Boolean(draftRegionId) && draftSelectedSiteIds.length > 0;

    const handleApply = () => {
        setError('');
        if (!canApply) {
            setError('Choose a site, or choose a region and at least one site location.');
            return;
        }

        const success = applySelection(
            draftMode === 'site'
                ? {
                    selectionMode: 'site',
                    contextId: draftSiteId,
                }
                : {
                    selectionMode: 'region',
                    contextId: draftRegionId,
                    selectedSiteIds: draftSelectedSiteIds,
                }
        );

        if (!success) {
            setError('That selection is no longer valid. Refresh the options and try again.');
        }
    };

    return (
        <Modal
            isOpen={selectorOpen}
            onDismiss={selectorBlocking ? undefined : closeSelector}
            isBlocking={selectorBlocking}
            styles={modalStyles}
        >
            <div style={modalShellStyles}>
                <div style={{
                    padding: '18px 24px 16px',
                    background: 'linear-gradient(180deg, #f37037 0%, #da6532 100%)',
                    color: '#ffffff',
                    borderBottom: '1px solid rgba(255,255,255,0.18)',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                        <div>
                            <Text variant="xLarge" styles={{ root: { color: '#ffffff', fontWeight: 700 } }}>
                                Choose Board Context
                            </Text>
                            <div style={{ marginTop: 4, fontSize: 13, color: '#fff0e8' }}>
                                Start from a single site location or from one region with some or all of its active site locations.
                            </div>
                        </div>
                        {!selectorBlocking && (
                            <DefaultButton onClick={closeSelector}>
                                Close
                            </DefaultButton>
                        )}
                    </div>
                </div>

                <div style={contentStyles}>
                    {error && (
                        <MessageBar messageBarType={MessageBarType.error} isMultiline>
                            {error}
                        </MessageBar>
                    )}

                    <div style={cardStyles}>
                        <Text variant="large" styles={{ root: { fontWeight: 700 } }}>
                            Selection Mode
                        </Text>
                        <div style={{ marginTop: 10 }}>
                            <ChoiceGroup
                                selectedKey={draftMode}
                                options={modeOptions}
                                onChange={(_, option) => {
                                    const nextMode = (option?.key ?? 'site') as TBoardContextType;
                                    setDraftMode(nextMode);
                                    setError('');
                                }}
                            />
                        </div>
                    </div>

                    {draftMode === 'site' && (
                        <div style={cardStyles}>
                            <div style={sectionTitleStyles}>
                                <Text variant="large" styles={{ root: { fontWeight: 700 } }}>
                                    Site Location
                                </Text>
                                <Text variant="small" styles={{ root: { color: '#52606d' } }}>
                                    Search all active site locations
                                </Text>
                            </div>

                            <div style={{ marginBottom: 8, fontSize: 12, color: '#64748b' }}>
                                {filteredSiteOptions.length > 0
                                    ? `Showing ${filteredSiteOptions.length} site location${filteredSiteOptions.length === 1 ? '' : 's'}. Type to narrow the list.`
                                    : 'No matching site locations found.'}
                            </div>
                            <ComboBox
                                label="Choose site location"
                                placeholder="Type to search site locations"
                                selectedKey={draftSiteId || undefined}
                                options={filteredSiteOptions}
                                text={draftSiteId ? (selectedSiteOption?.illumina_name ?? siteSearch) : siteSearch}
                                allowFreeform
                                autoComplete="on"
                                useComboBoxAsMenuWidth
                                onInputValueChange={(value) => {
                                    setSiteSearch(String(value ?? ''));
                                    setDraftSiteId('');
                                }}
                                onChange={(_, option, __, value) => {
                                    if (option) {
                                        setDraftSiteId(String(option.key ?? ''));
                                        setSiteSearch(String(option.text ?? ''));
                                        setError('');
                                        return;
                                    }

                                    const typedValue = String(value ?? '').trim().toLowerCase();
                                    const match = availableSites.find((site) => String(site.illumina_name ?? '').trim().toLowerCase() === typedValue);
                                    setDraftSiteId(match?.illumina_sitelocationid ?? '');
                                    setSiteSearch(String(value ?? ''));
                                }}
                            />
                        </div>
                    )}

                    {draftMode === 'region' && (
                        <>
                            <div style={cardStyles}>
                                <div style={sectionTitleStyles}>
                                    <Text variant="large" styles={{ root: { fontWeight: 700 } }}>
                                        Region
                                    </Text>
                                    <Text variant="small" styles={{ root: { color: '#52606d' } }}>
                                        Choose one active region
                                    </Text>
                                </div>

                                <div style={{ marginBottom: 8, fontSize: 12, color: '#64748b' }}>
                                    {filteredRegionOptions.length > 0
                                        ? `Showing ${filteredRegionOptions.length} region${filteredRegionOptions.length === 1 ? '' : 's'}. Type to narrow the list.`
                                        : 'No matching regions found.'}
                                </div>
                                <ComboBox
                                    label="Choose region"
                                    placeholder="Type to search regions"
                                    selectedKey={draftRegionId || undefined}
                                    options={filteredRegionOptions}
                                    text={draftRegionId ? (selectedRegionOption?.illumina_name ?? regionSearch) : regionSearch}
                                    allowFreeform
                                    autoComplete="on"
                                    useComboBoxAsMenuWidth
                                    onInputValueChange={(value) => {
                                        setRegionSearch(String(value ?? ''));
                                        setDraftRegionId('');
                                        setDraftSelectedSiteIds([]);
                                    }}
                                    onChange={(_, option, __, value) => {
                                        const nextRegionId = String(option?.key ?? '');
                                        if (!option) {
                                            const typedValue = String(value ?? '').trim().toLowerCase();
                                            const match = regions.find((region) => String(region.illumina_name ?? '').trim().toLowerCase() === typedValue);
                                            setDraftRegionId(String(match?.illumina_regionid ?? ''));
                                            setRegionSearch(String(value ?? ''));
                                            setDraftSelectedSiteIds(
                                                availableSites
                                                    .filter((site) => String(site._illumina_region_value ?? '').trim() === String(match?.illumina_regionid ?? ''))
                                                    .map((site) => site.illumina_sitelocationid)
                                            );
                                            return;
                                        }

                                        setDraftRegionId(nextRegionId);
                                        setRegionSearch(String(option.text ?? ''));
                                        setDraftSelectedSiteIds(
                                            availableSites
                                                .filter((site) => String(site._illumina_region_value ?? '').trim() === nextRegionId)
                                                .map((site) => site.illumina_sitelocationid)
                                        );
                                    }}
                                />
                            </div>

                            <div style={cardStyles}>
                                <div style={sectionTitleStyles}>
                                    <div>
                                        <Text variant="large" styles={{ root: { fontWeight: 700 } }}>
                                            Site Locations
                                        </Text>
                                        <div style={{ marginTop: 4, fontSize: 12, color: '#52606d' }}>
                                            {draftRegionId
                                                ? `${draftSelectedSiteIds.length} of ${regionSites.length} selected`
                                                : 'Choose a region first'}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <DefaultButton onClick={selectAllRegionSites} disabled={regionSites.length < 1}>
                                            Select All
                                        </DefaultButton>
                                        <DefaultButton onClick={clearAllRegionSites} disabled={regionSites.length < 1}>
                                            Clear All
                                        </DefaultButton>
                                    </div>
                                </div>

                                <SearchBox
                                    placeholder="Search site locations in this region"
                                    value={regionSiteSearch}
                                    onChange={(event, value) => setRegionSiteSearch(getInputValue(event, value))}
                                    disabled={!draftRegionId}
                                    styles={{ root: { marginBottom: 12 } }}
                                />
                                <div style={checkboxListStyles}>
                                    {filteredRegionSites.length > 0 ? filteredRegionSites.map((site) => (
                                        <Checkbox
                                            key={site.illumina_sitelocationid}
                                            label={site.illumina_name}
                                            checked={draftSelectedSiteIds.includes(site.illumina_sitelocationid)}
                                            onChange={(_, checked) => toggleRegionSite(site.illumina_sitelocationid, checked)}
                                            styles={{ root: { marginBottom: 8 } }}
                                        />
                                    )) : (
                                        <div style={{ padding: '8px 4px', color: '#64748b', fontSize: 13 }}>
                                            {draftRegionId ? 'No matching site locations found.' : 'No region selected yet.'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        {!selectorBlocking && (
                            <DefaultButton onClick={closeSelector}>
                                Cancel
                            </DefaultButton>
                        )}
                        <PrimaryButton onClick={handleApply} disabled={!canApply}>
                            Apply Selection
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export { BoardSelectionModal };
